/**
 * O bloco *Vínculo com o cadastro do IW* do detalhe do Caso (`TP-053`, `T7`, `D8`).
 *
 * Com um candidato só, o vínculo é automático e este bloco só mostra que ele existe, com "Trocar vínculo". Quando o
 * vínculo espera alguém (homônimo, novo no IW, desfeito, em conflito), a pessoa pede a busca, vê os candidatos que o IW
 * devolve AGORA e escolhe um, ou diz que é paciente novo. Os candidatos nunca são guardados: cada abertura busca de novo.
 */

import { useState } from 'react';
import Button from '@mui/material/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroDaApi } from '../../shared/api/erros';
import { ConfirmacaoHumana } from '../../shared/ui/ConfirmacaoHumana';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { AdmissaoNoIw } from './AdmissaoNoIw';
import { CadastroNoIw } from './CadastroNoIw';
import { chavesDeCasos } from './api/casos';
import { opcoesDosEnviosAoIw } from './api/enviosAoIw';
import { ROTULOS_DO_MOTIVO_DA_PENDENCIA, type CasoDetalhado } from './api/tipos';
import {
  ROTULOS_DO_MODO_DA_BUSCA,
  desfazerVinculoComIw,
  marcarPacienteNovoNoIw,
  opcoesDasAdmissoesDoPacienteNoIw,
  opcoesDosCandidatosDoIw,
  vincularAoIw,
  type CandidatoDoIw,
} from './api/vinculoIw';

/** Candidatos desenhados de uma vez: acima disso a lista vira rolagem e um aviso, não 200 botões. */
const MAXIMO_NA_TELA = 50;

/** Mensagem de cada desfecho, por `codigo`/`status` — mesma disciplina de `EsperasDoCaso.tsx`. */
function mensagemDeErroDoVinculo(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    switch (causa.codigo) {
      case 'IW_UNAVAILABLE':
        return 'O IW não respondeu agora. Tente de novo em instantes.';
      case 'IW_CREDENTIAL_REJECTED':
        return 'O IW recusou a credencial da plataforma para buscar pacientes. Avise o time do IW: falta liberar a permissão de busca.';
      case 'IW_PATIENT_NOT_IN_CANDIDATES':
        return 'Este paciente não está mais entre os candidatos do IW. A lista foi buscada de novo.';
      case 'IW_PATIENT_LINKED_ELSEWHERE':
        return 'Este paciente do IW acabou de ser vinculado a outro cadastro. Confira antes de juntar.';
      case 'CASE_ALREADY_LINKED_TO_IW':
        return 'O Caso já está vinculado a outro paciente do IW. Troque o vínculo antes de escolher.';
      case 'CASE_NOT_LINKED_TO_IW':
        return 'O Caso já não tinha vínculo com o IW.';
      case 'IW_LINK_NOT_PENDING':
        return 'O vínculo deste Caso já não está pendente.';
    }
    if (causa.status === 404) return 'O caso não foi encontrado para o seu núcleo.';
  }
  return 'Não foi possível concluir a ação. Tente novamente.';
}

/** `AAAA-MM-DD` do IW em `DD/MM/AAAA`, sem passar por `Date` (fuso não tem nada a ver com data de nascimento). */
function dataDeNascimento(valor: string | null): string {
  if (valor === null) return 'nascimento não informado no IW';
  const [ano, mes, dia] = valor.split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
}

function Candidato({
  candidato,
  ocupado,
  aoEscolher,
}: {
  candidato: CandidatoDoIw;
  ocupado: boolean;
  aoEscolher: () => void;
}): React.JSX.Element {
  const detalhes = [
    dataDeNascimento(candidato.nascimento),
    candidato.cpfMascarado !== null ? `CPF ${candidato.cpfMascarado}` : null,
    candidato.carteira !== null ? `carteira ${candidato.carteira}` : null,
    candidato.operadora,
  ].filter((parte): parte is string => parte !== null);

  return (
    <li className="anery-item">
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">
          {candidato.nome}
          {/* Texto, nunca só cor. É só uma marca: a ordem é a do IW e a escolha é da pessoa. */}
          {candidato.admissaoAtiva ? (
            <span className="ml-2 text-xs font-normal text-[var(--roxo)]">· admissão ativa no IW</span>
          ) : null}
        </span>
        <span className="num block text-xs text-[var(--tinta-2)]">{detalhes.join(' · ')}</span>
      </span>
      {/* O texto visível é "É este"; o nome acessível diz de QUEM, para o leitor de tela distinguir os homônimos. */}
      <Button
        size="small"
        variant="outlined"
        disabled={ocupado}
        onClick={aoEscolher}
        aria-label={`É este: ${candidato.nome}, ${dataDeNascimento(candidato.nascimento)}${
          candidato.admissaoAtiva ? ', admissão ativa no IW' : ''
        }${candidato.carteira !== null ? `, carteira ${candidato.carteira}` : ''}`}
      >
        É este
      </Button>
    </li>
  );
}

export function VinculoComOIw({
  caso,
  aoAtualizar,
}: {
  caso: CasoDetalhado;
  aoAtualizar: (caso: CasoDetalhado) => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [buscando, definirBuscando] = useState(false);
  const [trocando, definirTrocando] = useState(false);
  // O desfecho do "Cadastrar no IW" (NR-258) mora aqui: ele muda o vínculo, e o botão que o disparou sai da tela.
  const [avisoDoCadastro, definirAvisoDoCadastro] = useState('');

  const pendencia = caso.pendencias.find((p) => p.item === 'IwLink' && p.estado === 'Open') ?? null;
  const vinculado = !caso.pacienteProvisorio && pendencia === null;

  const candidatos = useQuery(opcoesDosCandidatosDoIw(caso.id, buscando && !vinculado));
  // O paciente que este Caso criou no IW (NR-258). Vínculo que nasceu do cadastro não se troca: o paciente é este por
  // construção, e trocar deixaria o paciente criado sem Caso no IW (a plataforma não apaga paciente lá; o servidor
  // recusa a troca). Só vale se o criado for o vinculado AGORA: vínculo refeito para outro paciente se troca.
  const envios = useQuery({ ...opcoesDosEnviosAoIw(caso.id), enabled: vinculado });
  // A mesma consulta do bloco da admissão (mesma chave): traz o paciente vinculado agora.
  const admissoes = useQuery(opcoesDasAdmissoesDoPacienteNoIw(caso.id, vinculado));
  const idVinculado = admissoes.data?.idPacienteVinculado ?? null;
  const criadoPeloCaso =
    envios.data?.find(
      (e) => e.tipo === 'CreatePatient' && e.estado === 'Completed' && e.idNoIw !== null && e.idNoIw === idVinculado,
    ) ?? null;

  // O vínculo mudou por fora (outra sessão, refetch do detalhe): a busca aberta não vale mais, e não pode disparar
  // sozinha se ele voltar a ficar pendente. Ajuste de estado na renderização, o padrão do React para "prop mudou".
  const [vinculadoAntes, definirVinculadoAntes] = useState(vinculado);
  if (vinculado !== vinculadoAntes) {
    definirVinculadoAntes(vinculado);
    if (vinculado) definirBuscando(false);
  }

  const depoisDeEscrever = (atualizado: CasoDetalhado): void => {
    aoAtualizar(atualizado);
    definirBuscando(false);
    clienteDeConsultas.removeQueries({ queryKey: chavesDeCasos.candidatosDoIw(caso.id) });
    void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.linhaDoTempo(caso.id) });
    void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.enviosAoIw(caso.id) });
  };

  // Erro que diz "a tela ficou velha": a lista de candidatos (422) ou o próprio Caso, que outra pessoa mudou (409).
  // Sem recarregar, a mensagem contradiria o que está na tela e a pessoa ficaria sem saída.
  const aoFalhar = (erro: unknown): void => {
    if (!(erro instanceof ErroDaApi)) return;
    if (erro.codigo === 'IW_PATIENT_NOT_IN_CANDIDATES') {
      void candidatos.refetch();
    } else if (erro.status === 409) {
      void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.detalhe(caso.id) });
    }
  };

  const escolher = useMutation({
    mutationFn: (idPaciente: string) => vincularAoIw(caso.id, idPaciente),
    onSuccess: depoisDeEscrever,
    onError: aoFalhar,
  });
  const pacienteNovo = useMutation({
    mutationFn: () => marcarPacienteNovoNoIw(caso.id),
    onSuccess: depoisDeEscrever,
    onError: aoFalhar,
  });
  const desfazer = useMutation({
    mutationFn: () => desfazerVinculoComIw(caso.id),
    onSuccess: depoisDeEscrever,
    onError: aoFalhar,
  });

  const ocupado = escolher.isPending || pacienteNovo.isPending || desfazer.isPending;
  const erroDaEscrita = escolher.error ?? pacienteNovo.error ?? desfazer.error;
  const limparErros = (): void => {
    definirAvisoDoCadastro('');
    escolher.reset();
    pacienteNovo.reset();
    desfazer.reset();
  };

  return (
    <div className="space-y-3">
      {vinculado ? (
        <>
          {/* role="status": o botão que tinha o foco some quando o vínculo muda, e o leitor de tela precisa ouvir o novo estado. */}
          <p role="status" className="text-[0.8125rem]">
            {criadoPeloCaso
              ? `Paciente criado no IW por este Caso (id ${criadoPeloCaso.idNoIw}) e vinculado a ele.`
              : 'Vinculado ao paciente do IW escolhido na busca. O nome do Caso passa a ser o que está no IW.'}
          </p>
          {/* NR-256: com o paciente do IW certo, a admissão pode ser aberta pela plataforma. */}
          <AdmissaoNoIw caso={caso} aoAtualizar={aoAtualizar} />
          {/* Enquanto os envios carregam não se sabe se o vínculo nasceu do cadastro: o botão só aparece depois. */}
          {criadoPeloCaso || envios.isPending || admissoes.isPending ? null : (
            <Button
              variant="outlined"
              disabled={ocupado}
              onClick={() => {
                limparErros();
                definirTrocando(true);
              }}
            >
              Trocar vínculo
            </Button>
          )}
        </>
      ) : (
        <>
          <p role="status" className="text-[0.8125rem]">
            {pendencia?.motivo != null
              ? ROTULOS_DO_MOTIVO_DA_PENDENCIA[pendencia.motivo]
              : 'O IW ainda não foi consultado para este Caso. A próxima rodada tenta de novo.'}
          </p>

          {/* NR-258: o Caso novo no IW pode virar paciente no IW pela plataforma, sem cadastro à mão. */}
          {pendencia?.motivo === 'NewInIw' ? (
            <CadastroNoIw caso={caso} aoAtualizar={depoisDeEscrever} aoAvisar={definirAvisoDoCadastro} />
          ) : null}

          {!buscando ? (
            <Button
              variant="outlined"
              onClick={() => {
                limparErros();
                definirBuscando(true);
              }}
            >
              Ver candidatos do IW
            </Button>
          ) : null}

          {buscando && candidatos.isPending ? <EstadoDeCarregamento linhas={2} /> : null}

          {buscando && candidatos.isError ? (
            <div className="space-y-2">
              <p role="alert" className="text-[0.8125rem] text-[var(--roxo)]">
                {mensagemDeErroDoVinculo(candidatos.error)}
              </p>
              <Button
                size="small"
                variant="outlined"
                disabled={candidatos.isFetching}
                onClick={() => {
                  void candidatos.refetch();
                }}
              >
                Buscar de novo
              </Button>
            </div>
          ) : null}

          {buscando && candidatos.isSuccess ? (
            <div className="space-y-2">
              <p className="text-xs text-[var(--tinta-2)]">
                {candidatos.data.modo === null
                  ? 'O Caso não tem carteira, CPF confirmado nem nome para buscar no IW.'
                  : `Busca ${ROTULOS_DO_MODO_DA_BUSCA[candidatos.data.modo]}: ${String(candidatos.data.candidatos.length)} ${
                      candidatos.data.candidatos.length === 1 ? 'paciente' : 'pacientes'
                    } no IW.`}
              </p>

              {/* Só o nome não identifica ninguém: o IW devolve todos com aquele nome, e escolher errado junta pessoas. */}
              {candidatos.data.modo === 'Name' && candidatos.data.candidatos.length > 0 ? (
                <p className="text-xs text-[var(--roxo)]">
                  Confira o nascimento e a carteira antes de escolher: só o nome não identifica o paciente.
                </p>
              ) : null}

              {candidatos.data.candidatos.length > MAXIMO_NA_TELA ? (
                <p className="text-xs text-[var(--tinta-2)]">
                  Mostrando {String(MAXIMO_NA_TELA)} de {String(candidatos.data.candidatos.length)}. Se o paciente não
                  estiver aqui, use o nascimento ou a carteira no pedido e busque de novo.
                </p>
              ) : null}

              {candidatos.data.candidatos.length > 0 ? (
                <ul className="max-h-96 list-none overflow-y-auto pl-0">
                  {candidatos.data.candidatos.slice(0, MAXIMO_NA_TELA).map((candidato) => (
                    <Candidato
                      key={candidato.idPaciente}
                      candidato={candidato}
                      // A lista que aparece enquanto o IW é consultado de novo (refetch, 422) ainda é a velha.
                      ocupado={ocupado || candidatos.isFetching}
                      aoEscolher={() => {
                        limparErros();
                        escolher.mutate(candidato.idPaciente);
                      }}
                    />
                  ))}
                </ul>
              ) : null}

              {/* "Paciente novo" só tem sentido quando ainda não é o que o Caso diz. */}
              {pendencia !== null && pendencia.motivo !== 'NewInIw' ? (
                <Button
                  variant="outlined"
                  disabled={ocupado}
                  onClick={() => {
                    limparErros();
                    pacienteNovo.mutate();
                  }}
                >
                  Nenhum destes: é paciente novo
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {avisoDoCadastro !== '' ? (
        <p role="status" className="text-[0.8125rem]">
          {avisoDoCadastro}
        </p>
      ) : null}

      {erroDaEscrita ? (
        <p role="alert" className="text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDoVinculo(erroDaEscrita)}
        </p>
      ) : null}

      <ConfirmacaoHumana
        aberto={trocando}
        titulo="Trocar o vínculo com o IW?"
        descricao="O Caso passa a ter um paciente provisório com o nome do e-mail e volta a esperar a escolha do paciente certo do IW. Os outros Casos do paciente atual continuam vinculados a ele."
        rotuloDeConfirmar="Trocar vínculo"
        aoCancelar={() => {
          definirTrocando(false);
        }}
        aoConfirmar={() => {
          definirTrocando(false);
          desfazer.mutate();
        }}
      />
    </div>
  );
}
